import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { analytics } from "@heycatch/sdk";
import { api, auth } from "../lib/api";

const AuthContext = createContext(null);

// Identify the current user with HeyCatch. Called on login, register, and
// on the mount /auth/me refresh so a returning-cookie session still ends
// up attributed. `setIdentity` is a no-op before analytics.init runs (that
// happens in src/index.js at module scope) — so it's safe to call anywhere.
function _identify(u) {
  if (!u || !u.id) return;
  try {
    analytics.setIdentity(
      u.id,
      { email: u.email, name: u.name, plan: u.subscribed ? "monthly" : "free" },
      { signup_date: u.created_at || undefined },
    );
  } catch { /* SDK swallows its own errors, this is defensive */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(auth.getUser());
  const [loading, setLoading] = useState(false);
  // Tracks the last-known `subscribed` value so we can detect the
  // false → true transition and fire `subscription_started` exactly once
  // per client session. The real source-of-truth remains the Stripe
  // webhook on the FastAPI backend; this is the client-side complement
  // since we don't have a JS server to send from.
  const wasSubscribedRef = useRef(!!(auth.getUser() && auth.getUser().subscribed));

  // Any state that updates `user` also feeds HeyCatch identity + a
  // subscription-transition check. Centralised so login / register /
  // refresh / subscribe / setSession all get the same treatment.
  const applyUser = (u) => {
    setUser(u);
    if (!u) return;
    _identify(u);
    const nowSubbed = !!u.subscribed;
    if (nowSubbed && !wasSubscribedRef.current) {
      try {
        analytics.trackEvent("subscription_started", { plan: "monthly" });
      } catch { /* ignore */ }
    }
    wasSubscribedRef.current = nowSubbed;
  };

  useEffect(() => {
    // Always re-verify the session against the server on mount if we have a
    // token — this catches the case where a Stripe webhook (or an admin
    // toggle) flipped `subscribed` server-side AFTER the user's cached JWT
    // was minted. Without this, a paying subscriber whose payment landed
    // AFTER their login would still see the free-tier paywall until they
    // logged out and back in. Fails silently — the axios 401 interceptor
    // already handles expired / revoked tokens.
    const refresh = async () => {
      if (!auth.getToken()) return;
      try {
        const r = await api.get("/auth/me");
        auth.setUser(r.data);
        applyUser(r.data);
      } catch (e) {
        /* token invalid handled by interceptor */
      }
    };
    refresh();
    // Also identify from cached user on cold mount so returning visitors
    // are known before /auth/me resolves.
    const cached = auth.getUser();
    if (cached) _identify(cached);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      auth.setToken(r.data.access_token);
      auth.setUser(r.data.user);
      applyUser(r.data.user);
      return r.data.user;
    } finally { setLoading(false); }
  };

  const register = async (name, email, password) => {
    setLoading(true);
    try {
      const r = await api.post("/auth/register", { name, email, password });
      auth.setToken(r.data.access_token);
      auth.setUser(r.data.user);
      applyUser(r.data.user);
      // Only the client knows sign-up just completed on this device — send
      // the business event from here per HeyCatch docs' client-only SPA
      // pattern. Server can't add anything: the FastAPI backend already
      // handled auth but has no JS SDK.
      try { analytics.trackEvent("signup_completed"); } catch { /* ignore */ }
      return r.data.user;
    } finally { setLoading(false); }
  };

  const logout = () => {
    auth.clear();
    setUser(null);
    wasSubscribedRef.current = false;
    try { analytics.resetIdentity(); } catch { /* ignore */ }
    window.location.href = "/";
  };

  const subscribe = async () => {
    const r = await api.post("/subscribe/checkout");
    auth.setToken(r.data.access_token);
    auth.setUser(r.data.user);
    applyUser(r.data.user);
    return r.data.user;
  };

  // Used by password reset — sets the session directly from a token + user
  const setSession = (token, u) => {
    auth.setToken(token);
    auth.setUser(u);
    applyUser(u);
  };

  return (
    <AuthContext.Provider value={{ user, setUser: applyUser, loading, login, register, logout, subscribe, setSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
